/**
 * heroCta — the ONE place that decides what a hero slide's primary CTA points
 * at.
 *
 * WHY THIS IS A HELPER AND NOT A CONDITION IN THE SLIDER
 * -------------------------------------------------------
 * A hero slide's `cta_link` can be EITHER of two shapes, and they have to be
 * rendered in two DIFFERENT ways:
 *
 *   - a root-relative path (`/products/values-incubator`) — an internal page.
 *     The renderer prefixes it with the active locale (`/en/products/...`),
 *     because a bare path is bounced by the bare-path redirect map into the
 *     DEFAULT locale, throwing an English reader back into Chinese. It is
 *     rendered as a React Router `<Link>`, so the SPA routes it.
 *   - an absolute `http(s)` URL (`https://csp.yada.hk`) — another site. CSP
 *     lives on its own domain, NOT under www.yada.hk. It MUST be a plain
 *     `<a target="_blank">`: `buildLocalePath()` would turn it into the
 *     nonsense path `/zh-Hanthttps://csp.yada.hk`, and a Router `<Link>`
 *     would try to route it inside the SPA.
 *
 * The rule for telling the two apart is deliberately trivial:
 *
 *   - a value that starts with `/` is INTERNAL;
 *   - a value that is an absolute `http:` / `https:` URL is EXTERNAL;
 *   - anything else is refused outright and renders as plain text.
 *
 * It is decided HERE, once, because the same value is checked twice — once on
 * the way IN (the CMS write schema, `server/routes/hero-slides.ts`) and once
 * on the way OUT (the render) — and two copies of one rule is how they drift
 * apart. A value the admin form ACCEPTS but the renderer REFUSES is a dead
 * button; a value the admin form REFUSES but the renderer HONOURS is a hole
 * in the write path.
 *
 * THE URL POLICY IS NOT INVENTED HERE
 * -----------------------------------
 * The checks themselves are the site's existing ones:
 * `safeInternalLink` (a root-relative path, returned as-is) and
 * `safeExternalUrl` (http(s) only) from `src/utils/safeExternalUrl.ts`. The
 * server half of the same policy lives in `server/lib/safe-url.ts` — a
 * documented twin, because the server tsconfig has `rootDir: "./"` and
 * includes only `server/**`, so a `src/` import would break the server
 * build. `scripts/test-hero-slides.mts` asserts that the two halves agree,
 * vector by vector.
 *
 * The one rule this helper adds on top of both policies is the WHITESPACE
 * refusal for the internal branch. The server refuses a path containing one
 * (a space in a path is a URL-encoding accident waiting to happen), and the
 * renderer has to agree or the button would be dead on arrival. A stored
 * value can never carry whitespace — the write path refuses it — so this
 * guard only ever fires on a hand-edited row, and it makes the renderer
 * refuse that row too instead of trusting it.
 *
 * IMPORT CONSTRAINTS
 * ------------------
 * This module is imported by the browser bundle AND by a Node script
 * (`scripts/test-hero-slides.mts`, `scripts/lint-hero-slots.ts`), so:
 *   - the import of the URL policy is RELATIVE (`../utils/safeExternalUrl`),
 *     so it resolves the same way under tsx and under Vite, with no
 *     dependence on a bundler alias;
 *   - there is no CSS import and no React import, so the module can be
 *     loaded outside a bundler.
 */

import { safeExternalUrl, safeInternalLink } from '../utils/safeExternalUrl'

/**
 * What a hero CTA points at.
 *
 *   - `internal` — a root-relative path; the renderer prefixes it with the
 *     active locale and renders it as a Router `<Link>`.
 *   - `external` — an absolute http(s) URL on another site; the renderer
 *     renders it as a plain `<a target="_blank" rel="noopener noreferrer">`.
 *   - `none` — the value is refused by the URL policy (a `javascript:` URL,
 *     a `data:` URL, a bare host, a protocol-relative `//evil.example`, a
 *     relative path with no leading slash). The CTA renders as plain text:
 *     nothing untrusted ever reaches an `href` or a router `to`.
 */
export type HeroCtaTarget =
  | { kind: 'internal'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'none'; href: null }

/**
 * Decide what a stored `cta_link` points at.
 *
 * The value comes from the CMS, so it is untrusted input; the return value is
 * what may safely be put in an `href` (external) or in a Router `to`
 * (internal, after the locale prefix is applied).
 */
export function resolveHeroCta(value: string | null | undefined): HeroCtaTarget {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  if (!trimmed) return { kind: 'none', href: null }

  // No whitespace ANYWHERE — not in a path, not in a query string. This
  // mirrors the server's write schema exactly: a value the admin form
  // ACCEPTS but the renderer REFUSES is a dead button, and vice versa.
  // (`new URL()` happily percent-encodes a space into `%20`, so the URL
  // policy alone would not catch it.)
  if (/\s/.test(trimmed)) return { kind: 'none', href: null }

  // A `/`-prefixed value is INTERNAL. `safeInternalLink` returns a
  // root-relative path AS-IS (no normalisation, no invented hostname) and
  // refuses the protocol-relative `//host` and its backslash disguise.
  if (trimmed.startsWith('/')) {
    const href = safeInternalLink(trimmed)
    if (href) return { kind: 'internal', href }
  }

  // Otherwise it may only be an absolute http(s) URL — EXTERNAL.
  // `safeExternalUrl` refuses every other scheme, a bare host and anything
  // `new URL()` cannot parse.
  const href = safeExternalUrl(trimmed)
  if (href) return { kind: 'external', href }

  return { kind: 'none', href: null }
}
